import { useCallback, useEffect, useMemo, useRef, useState, type DragEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Background,
  BackgroundVariant,
  Controls,
  MiniMap,
  Panel,
  ReactFlow,
  ReactFlowProvider,
  useEdgesState,
  useNodesState,
  useReactFlow,
  type Connection,
  type Edge,
  type Node,
  type XYPosition,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { Loader2, Lock, MousePointerClick, Wand2 } from 'lucide-react';
import { NodoPasso, type DatiNodo } from '../components/email/automazioni/NodoPasso';
import { FrecciaSequenza } from '../components/email/automazioni/FrecciaSequenza';
import { PannelloPasso } from '../components/email/automazioni/PannelloPasso';
import { PannelloPartenza } from '../components/email/automazioni/PannelloPartenza';
import { ConfirmDeleteDialog } from '../components/ui/confirm-delete-dialog';
import { ComponiEmailModal } from '../components/email/automazioni/ComponiEmailModal';
import { TestataSequenza } from '../components/email/automazioni/TestataSequenza';
import { TavolozzaPassi } from '../components/email/automazioni/TavolozzaPassi';
import {
  ASPETTO_PASSI,
  TIPO_TRASCINATO,
  eTipoDiPasso,
} from '../components/email/automazioni/aspettoPassi';
import { automationService, type EtichettaScelta } from '../lib/email/automationService';
import {
  configPartenza,
  descriviPartenza,
  entraTuttaLaLista,
  partenzaDi,
  type Partenza,
} from '../lib/email/partenzaSequenza';
import {
  ID_INIZIO,
  TIPO_FRECCIA,
  datiNodo,
  eUnaPartenza,
  freccia,
  grafoInPassi,
  haScadenza,
  passiInGrafo,
  passoNuovo,
} from '../lib/email/disegnoSequenza';
import {
  ALTEZZA_NODO,
  LARGHEZZA_NODO,
  aggancio,
  ordinaGrafo,
  posizioneLibera,
} from '../lib/email/posizioniSequenza';
import { useNotifications } from '../store/useStore';
import {
  type ConfigInvio,
  type EmailAutomation,
  type EmailAutomationStep,
  type StepKind,
} from '../types/automazioni.types';

const TIPI_NODO = { passo: NodoPasso };
const TIPI_FRECCIA = { [TIPO_FRECCIA]: FrecciaSequenza };

/** Il tempo che la lavagna impiega a scorrere fino a una casella. */
const DURATA_SCORRIMENTO = 350;
/** Il pannello a destra si apre e restringe la lavagna: si aspetta che sia fatto. */
const ATTESA_PANNELLO = 80;

function stessiDati(a: DatiNodo, b: DatiNodo): boolean {
  return (
    a.kind === b.kind &&
    a.titolo === b.titolo &&
    a.riassunto === b.riassunto &&
    a.manca === b.manca &&
    a.scollegata === b.scollegata &&
    a.conScadenza === b.conScadenza
  );
}

export default function EmailAutomationEditorPage() {
  const { id } = useParams<{ id: string }>();
  // Una sequenza diversa riparte da zero: niente disegno vecchio a schermo
  // mentre arriva quello nuovo, e niente salvataggio di quello appena letto.
  return (
    <ReactFlowProvider key={id}>
      <EditorSequenza />
    </ReactFlowProvider>
  );
}

function EditorSequenza() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { addNotification } = useNotifications();
  const { screenToFlowPosition, setCenter, getZoom, fitView, deleteElements } = useReactFlow();

  const [automazione, setAutomazione] = useState<EmailAutomation | null>(null);
  const [passi, setPassi] = useState<EmailAutomationStep[]>([]);
  const [etichette, setEtichette] = useState<EtichettaScelta[]>([]);
  const [sceltoId, setSceltoId] = useState<string | null>(null);
  const [componiAperto, setComponiAperto] = useState(false);
  const [problemi, setProblemi] = useState<string[] | null>(null);
  const [confermaAccensione, setConfermaAccensione] = useState(false);

  const [nodi, setNodi, onNodiCambiati] = useNodesState<Node<DatiNodo>>([]);
  const [archi, setArchi, onArchiCambiati] = useEdgesState<Edge>([]);

  const [caricando, setCaricando] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const primoCaricamento = useRef(true);
  const lavagnaRef = useRef<HTMLDivElement>(null);

  const nomiEtichette = useMemo(
    () => Object.fromEntries(etichette.map((t) => [t.id, t.name])),
    [etichette]
  );

  /**
   * Quello che del disegno finisce nel database: dove stanno le caselle e
   * come sono collegate. Scegliere una casella o una freccia non lo cambia,
   * quindi non fa partire un salvataggio.
   */
  const firmaDisegno = useMemo(
    () =>
      JSON.stringify([
        nodi.map((n) => [n.id, Math.round(n.position.x), Math.round(n.position.y)]),
        archi.map((a) => [a.source, a.sourceHandle, a.target]),
      ]),
    [nodi, archi]
  );

  const modificabile = automazione?.status !== 'attiva';
  const scelto = passi.find((p) => p.id === sceltoId) ?? null;
  const partenza = automazione ? descriviPartenza(automazione, nomiEtichette) : '';

  // --- Caricamento ---------------------------------------------------------
  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        const [disegno, tags] = await Promise.all([
          automationService.dettaglio(id),
          automationService.etichette(),
        ]);
        setAutomazione(disegno.automazione);
        setPassi(disegno.passi);
        setEtichette(tags);

        const nomi = Object.fromEntries(tags.map((t) => [t.id, t.name]));
        const grafo = passiInGrafo(
          disegno.passi,
          disegno.automazione.first_step_id,
          nomi,
          descriviPartenza(disegno.automazione, nomi)
        );
        setNodi(grafo.nodi);
        setArchi(grafo.archi);

        // Cosa manca si sa subito, non solo dopo la prima modifica.
        if (disegno.automazione.status !== 'attiva') {
          automationService.valida(id).then(setProblemi, () => setProblemi(null));
        }
      } catch (errore) {
        addNotification({
          type: 'error',
          title: 'Sequenza non trovata',
          message: errore instanceof Error ? errore.message : 'Errore imprevisto.',
        });
        navigate('/mailing/sequenze');
      } finally {
        setCaricando(false);
      }
    })();
    // Si carica una volta per sequenza: addNotification, navigate e i setter
    // non cambiano fra un disegno e l'altro.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Il contenuto delle caselle cambia dal pannello e dalle frecce: qui si
  // aggiorna solo quello che si legge sulla lavagna, lasciando le posizioni
  // dove stanno. Se non cambia niente la lavagna resta com'e' e non si
  // ridisegna per niente.
  useEffect(() => {
    const raggiunte = new Set(archi.map((a) => a.target));
    setNodi((precedenti) => {
      let cambiato = false;
      const aggiornati = precedenti.map((nodo) => {
        if (nodo.id === ID_INIZIO) return nodo;
        const passo = passi.find((p) => p.id === nodo.id);
        if (!passo) return nodo;
        // Alle partenze ("ogni volta che entra in una lista") non arriva
        // nessuna freccia per definizione: non sono "non collegate".
        const scollegata = !eUnaPartenza(passo.kind) && !raggiunte.has(nodo.id);
        const dati = { ...datiNodo(passo, nomiEtichette), scollegata };
        if (stessiDati(nodo.data, dati)) return nodo;
        cambiato = true;
        return { ...nodo, data: dati };
      });
      return cambiato ? aggiornati : precedenti;
    });
  }, [passi, archi, nomiEtichette, setNodi]);

  // Tolto il tempo massimo a un'attesa di una lista, l'uscita "Scaduto"
  // sparisce: la sua freccia non deve restare appesa al nulla.
  useEffect(() => {
    const senzaScadenza = new Set(
      passi.filter((p) => p.kind === 'attendi_evento' && !haScadenza(p)).map((p) => p.id)
    );
    setArchi((precedenti) => {
      const tenuti = precedenti.filter(
        (a) => !(a.sourceHandle === 'scaduta' && senzaScadenza.has(a.source))
      );
      return tenuti.length === precedenti.length ? precedenti : tenuti;
    });
  }, [passi, setArchi]);

  // --- Salvataggio automatico ---------------------------------------------
  const salva = useCallback(async () => {
    if (!id || !modificabile) return;
    setSalvando(true);
    try {
      const esito = grafoInPassi(passi, nodi, archi);
      await automationService.salvaDisegno(id, esito.firstStepId, esito.passi);
      setProblemi(await automationService.valida(id));
    } catch (errore) {
      addNotification({
        type: 'error',
        title: 'Non riesco a salvare',
        message: errore instanceof Error ? errore.message : 'Errore imprevisto.',
      });
    } finally {
      setSalvando(false);
    }
  }, [id, modificabile, passi, nodi, archi, addNotification]);

  useEffect(() => {
    if (caricando || !modificabile) return;
    if (primoCaricamento.current) {
      primoCaricamento.current = false;
      return;
    }
    const attesa = setTimeout(salva, 1200);
    return () => clearTimeout(attesa);
    // Si salva quando cambia il contenuto (passi e firmaDisegno), non a ogni
    // nuova versione di salva: quella cambia anche per una semplice selezione.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [passi, firmaDisegno, caricando, modificabile]);

  // --- Lavagna -------------------------------------------------------------
  const collega = useCallback(
    (connessione: Connection) => {
      if (!connessione.source || !connessione.target) return;
      const { source, target } = connessione;
      const uscita = connessione.sourceHandle ?? 'out';
      // Da un attacco esce una freccia sola: la nuova sostituisce la
      // vecchia, altrimenti il percorso si biforcherebbe senza dirlo.
      setArchi((precedenti) => [
        ...precedenti.filter((a) => !(a.source === source && (a.sourceHandle ?? 'out') === uscita)),
        freccia(source, uscita, target),
      ]);
    },
    [setArchi]
  );

  /** Trascinare la punta di una freccia su un'altra casella la sposta li'. */
  const ricollega = useCallback(
    (vecchia: Edge, nuova: Connection) => {
      setArchi((precedenti) => precedenti.filter((a) => a.id !== vecchia.id));
      collega(nuova);
    },
    [setArchi, collega]
  );

  // "Si parte" dice da dove entrano i contatti: se la scelta cambia dal
  // pannello, cambia anche la scritta sulla lavagna.
  useEffect(() => {
    setNodi((precedenti) =>
      precedenti.map((n) =>
        n.id === ID_INIZIO && n.data.riassunto !== partenza
          ? { ...n, data: { ...n.data, riassunto: partenza } }
          : n
      )
    );
  }, [partenza, setNodi]);

  const cambiaPartenza = async (nuova: Partenza) => {
    if (!automazione) return;
    const campi = { trigger_kind: nuova.trigger, trigger_config: configPartenza(nuova) };
    setAutomazione({ ...automazione, ...campi });
    try {
      await automationService.aggiorna(automazione.id, campi);
      setProblemi(await automationService.valida(automazione.id));
    } catch (errore) {
      addNotification({
        type: 'error',
        title: 'Non riesco a salvare la partenza',
        message: errore instanceof Error ? errore.message : 'Errore imprevisto.',
      });
    }
  };

  const cambiaServizio = async (servizio: boolean) => {
    if (!automazione) return;
    setAutomazione({ ...automazione, servizio });
    try {
      await automationService.aggiorna(automazione.id, { servizio });
    } catch (errore) {
      addNotification({
        type: 'error',
        title: 'Non riesco a salvare cosa manda la sequenza',
        message: errore instanceof Error ? errore.message : 'Errore imprevisto.',
      });
    }
  };

  const chiudiPannello = useCallback(() => {
    setSceltoId(null);
    setNodi((precedenti) => precedenti.map((n) => (n.selected ? { ...n, selected: false } : n)));
  }, [setNodi]);

  /** Il punto della lavagna che si vede al centro dello schermo. */
  const centroVisibile = useCallback((): XYPosition => {
    const riquadro = lavagnaRef.current?.getBoundingClientRect();
    if (!riquadro) return { x: 0, y: 0 };
    const centro = screenToFlowPosition({
      x: riquadro.left + riquadro.width / 2,
      y: riquadro.top + riquadro.height / 2,
    });
    return { x: centro.x - LARGHEZZA_NODO / 2, y: centro.y - ALTEZZA_NODO / 2 };
  }, [screenToFlowPosition]);

  /**
   * Aggiunge una casella dove si vede. Col tasto va sotto la casella scelta
   * (gia' collegata) oppure al centro della vista; trascinata va dove la
   * lasci. In ogni caso la lavagna la porta in vista e si apre il pannello.
   */
  const aggiungi = useCallback(
    (kind: StepKind, puntoLasciato?: XYPosition) => {
      if (!id) return;
      // "Ogni volta che entra in una lista" e' una partenza: non si appende
      // sotto la casella scelta, va in un posto libero per conto suo.
      const attacco =
        puntoLasciato || kind === 'ingresso_evento' ? null : aggancio(sceltoId, nodi, archi);
      const voluta = puntoLasciato
        ? { x: puntoLasciato.x - LARGHEZZA_NODO / 2, y: puntoLasciato.y - ALTEZZA_NODO / 2 }
        : centroVisibile();
      const posizione = attacco?.posizione ?? posizioneLibera(voluta, nodi);
      const nuovo = passoNuovo(id, kind, posizione);

      setPassi((p) => [...p, nuovo]);
      setNodi((precedenti) => [
        ...precedenti.map((n) => (n.selected ? { ...n, selected: false } : n)),
        {
          id: nuovo.id,
          type: 'passo',
          position: posizione,
          selected: true,
          data: datiNodo(nuovo, nomiEtichette),
        },
      ]);
      if (attacco) {
        setArchi((precedenti) => [
          ...precedenti,
          freccia(attacco.sorgente, attacco.uscita, nuovo.id),
        ]);
      }
      setSceltoId(nuovo.id);

      if (!puntoLasciato) {
        setTimeout(() => {
          setCenter(posizione.x + LARGHEZZA_NODO / 2, posizione.y + ALTEZZA_NODO / 2, {
            zoom: getZoom(),
            duration: DURATA_SCORRIMENTO,
          });
        }, ATTESA_PANNELLO);
      }
    },
    [id, sceltoId, nodi, archi, nomiEtichette, centroVisibile, setNodi, setArchi, setCenter, getZoom]
  );

  const suTrascinaSopra = useCallback((evento: DragEvent) => {
    if (!evento.dataTransfer.types.includes(TIPO_TRASCINATO)) return;
    evento.preventDefault();
    evento.dataTransfer.dropEffect = 'move';
  }, []);

  const suLascia = useCallback(
    (evento: DragEvent) => {
      const kind = evento.dataTransfer.getData(TIPO_TRASCINATO);
      if (!eTipoDiPasso(kind)) return;
      evento.preventDefault();
      aggiungi(kind, screenToFlowPosition({ x: evento.clientX, y: evento.clientY }));
    },
    [aggiungi, screenToFlowPosition]
  );

  /** Le caselle tolte, dal pannello o col tasto canc, spariscono anche dal salvataggio. */
  const suCaselleTolte = useCallback((tolte: Node[]) => {
    const ids = new Set(tolte.map((n) => n.id));
    setPassi((p) => p.filter((x) => !ids.has(x.id)));
    setSceltoId((attuale) => (attuale && ids.has(attuale) ? null : attuale));
  }, []);

  const elimina = useCallback(
    (passoId: string) => deleteElements({ nodes: [{ id: passoId }] }),
    [deleteElements]
  );

  const mettiInOrdine = useCallback(() => {
    const posizioni = ordinaGrafo(nodi, archi);
    setNodi((precedenti) =>
      precedenti.map((n) => {
        const posizione = posizioni.get(n.id);
        return posizione ? { ...n, position: posizione } : n;
      })
    );
    setTimeout(() => fitView({ padding: 0.2, maxZoom: 1, duration: 400 }), 50);
  }, [nodi, archi, setNodi, fitView]);

  // --- Accensione ----------------------------------------------------------
  const accendi = async () => {
    if (!id || !automazione) return;
    await salva();
    const entratiPrima = automazione.entrati_count;
    const trovati = await automationService.accendi(id);
    setProblemi(trovati);

    if (trovati.length > 0) {
      addNotification({
        type: 'warning',
        title: 'La sequenza non si accende',
        message: trovati[0],
      });
      return;
    }
    setSceltoId(null);

    // Con "tutta la lista" i contatti entrano gia' accendendo: si rilegge
    // la sequenza per dire quanti.
    if (entraTuttaLaLista(automazione)) {
      const { automazione: aggiornata } = await automationService.dettaglio(id);
      setAutomazione(aggiornata);
      const entrati = aggiornata.entrati_count - entratiPrima;
      addNotification({
        type: 'success',
        title: 'Sequenza accesa',
        message:
          entrati === 1
            ? 'E\' entrato 1 contatto della lista. Chi aggiungi dopo entra da solo.'
            : `Sono entrati ${entrati} contatti della lista. Chi aggiungi dopo entra da solo.`,
      });
      return;
    }

    setAutomazione((a) => (a ? { ...a, status: 'attiva' } : a));
    addNotification({
      type: 'success',
      title: 'Sequenza accesa',
      message: 'Da adesso i contatti ci entrano e ricevono.',
    });
  };

  /** Prima di scrivere a tutta una lista si chiede: e' l'unico tasto che
   *  manda posta a decine di persone in un colpo. */
  const chiediAccensione = () => {
    if (automazione && entraTuttaLaLista(automazione)) {
      setConfermaAccensione(true);
      return;
    }
    accendi();
  };

  const listaDiPartenza = etichette.find((t) => t.id === automazione?.trigger_config?.tag_id);

  const metti_in_pausa = async () => {
    if (!id) return;
    await automationService.metti_in_pausa(id);
    setAutomazione((a) => (a ? { ...a, status: 'sospesa' } : a));
    addNotification({
      type: 'info',
      title: 'Sequenza in pausa',
      message: 'Chi e\' dentro resta fermo dov\'e\'. Nessuno nuovo entra.',
    });
  };

  if (caricando) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-neutral-400" />
      </div>
    );
  }

  if (!automazione) return null;

  return (
    <div className="h-[calc(100vh-7rem)] flex flex-col">
      <TestataSequenza
        automazione={automazione}
        partenza={partenza}
        problemi={problemi}
        salvando={salvando}
        onIndietro={() => navigate('/mailing/sequenze')}
        onRinomina={(name) => setAutomazione({ ...automazione, name })}
        onNomeConfermato={() =>
          automationService.aggiorna(automazione.id, { name: automazione.name })
        }
        onAccendi={chiediAccensione}
        onPausa={metti_in_pausa}
      />

      {/* La lavagna */}
      <div className="relative flex-1 flex min-h-0 border border-neutral-200 overflow-hidden bg-neutral-50">
        <div ref={lavagnaRef} className="flex-1 min-w-0">
          <ReactFlow
            nodes={nodi}
            edges={archi}
            onNodesChange={onNodiCambiati}
            onEdgesChange={onArchiCambiati}
            onNodesDelete={suCaselleTolte}
            onConnect={collega}
            onReconnect={ricollega}
            onNodeClick={(_, nodo) => setSceltoId(nodo.id)}
            onPaneClick={() => setSceltoId(null)}
            onDragOver={suTrascinaSopra}
            onDrop={suLascia}
            nodeTypes={TIPI_NODO}
            edgeTypes={TIPI_FRECCIA}
            nodesDraggable={modificabile}
            nodesConnectable={modificabile}
            elementsSelectable={modificabile}
            edgesReconnectable={modificabile}
            deleteKeyCode={modificabile ? ['Backspace', 'Delete'] : null}
            connectionLineStyle={{ stroke: '#525252', strokeWidth: 1.75, strokeDasharray: '5 4' }}
            fitView
            fitViewOptions={{ padding: 0.25, maxZoom: 1 }}
            minZoom={0.2}
            maxZoom={1.75}
            proOptions={{ hideAttribution: false }}
          >
            <Background variant={BackgroundVariant.Dots} gap={18} size={1.2} color="#d4d4d4" />

            {modificabile && (
              <Panel position="top-left">
                <TavolozzaPassi onAggiungi={(kind) => aggiungi(kind)} />
              </Panel>
            )}

            {modificabile && passi.length > 1 && (
              <Panel position="top-right">
                <button
                  type="button"
                  onClick={mettiInOrdine}
                  className="inline-flex items-center gap-1.5 h-8 px-3 bg-white border border-neutral-200 text-xs font-medium text-neutral-700 shadow-sm hover:border-neutral-300 hover:text-neutral-900 transition-colors"
                >
                  <Wand2 className="w-3.5 h-3.5" />
                  Metti in ordine
                </button>
              </Panel>
            )}

            {modificabile && passi.length === 0 && (
              <Panel position="top-center">
                <div className="flex items-center gap-2.5 bg-white border border-neutral-200 shadow-sm px-4 py-2.5 mt-10 text-xs text-neutral-600">
                  <MousePointerClick className="w-4 h-4 text-neutral-400 flex-shrink-0" />
                  Scegli la prima casella dai tasti a sinistra: si attacca da sola a "Si parte".
                </div>
              </Panel>
            )}

            {!modificabile && (
              <Panel position="top-center">
                <div className="flex items-center gap-2 bg-neutral-900 text-white px-3.5 py-2 text-xs shadow-md">
                  <Lock className="w-3.5 h-3.5 text-white/60" />
                  In funzione: il percorso non si tocca. Mettila in pausa per cambiarlo, chi e'
                  dentro riprende da dov'era.
                </div>
              </Panel>
            )}

            <Controls showInteractive={false} position="bottom-left" />
            <MiniMap
              position="bottom-right"
              pannable
              zoomable
              nodeColor={(n) => ASPETTO_PASSI[(n.data as DatiNodo).kind].esadecimale}
              nodeBorderRadius={2}
              maskColor="rgba(250,250,250,0.75)"
              className="!border !border-neutral-200 !shadow-sm"
              style={{ width: 150, height: 100 }}
            />
          </ReactFlow>
        </div>

        {sceltoId === ID_INIZIO && modificabile && (
          <PannelloPartenza
            partenza={partenzaDi(automazione)}
            servizio={automazione.servizio ?? false}
            liste={etichette}
            onChange={cambiaPartenza}
            onCambiaServizio={cambiaServizio}
            onChiudi={chiudiPannello}
          />
        )}

        {scelto && modificabile && (
          <PannelloPasso
            passo={scelto}
            invii={passi.filter((p) => p.kind === 'invio' && p.id !== scelto.id)}
            etichette={etichette}
            onChange={(aggiornato) =>
              setPassi((p) => p.map((x) => (x.id === aggiornato.id ? aggiornato : x)))
            }
            onComponi={() => setComponiAperto(true)}
            onElimina={() => elimina(scelto.id)}
            onChiudi={chiudiPannello}
          />
        )}
      </div>

      <ConfirmDeleteDialog
        open={confermaAccensione}
        onOpenChange={setConfermaAccensione}
        onConfirm={() => {
          setConfermaAccensione(false);
          accendi();
        }}
        title="Accendo e scrivo a tutta la lista?"
        description={
          listaDiPartenza
            ? `Entrano subito i ${
                automazione.servizio
                  ? listaDiPartenza.ricevono_servizio
                  : listaDiPartenza.possono_ricevere
              } contatti di "${listaDiPartenza.name}" che possono ricevere, e ricevono la sequenza dall'inizio. Le email partono nell'orario consentito, rispettando il tetto giornaliero.`
            : 'Entrano subito tutti i contatti della lista che possono ricevere.'
        }
        confirmLabel="Accendi"
        confirmClassName="bg-primary-600 hover:bg-primary-700"
      />

      {scelto?.kind === 'invio' && (
        <ComponiEmailModal
          aperto={componiAperto}
          passo={scelto}
          onChangeConfig={(parziale) =>
            setPassi((p) =>
              p.map((x) =>
                x.id === scelto.id
                  ? { ...x, config: { ...(x.config as ConfigInvio), ...parziale } }
                  : x
              )
            )
          }
          onChiudi={setComponiAperto}
        />
      )}
    </div>
  );
}
